import { ApplicationConfig, inject, provideAppInitializer, provideBrowserGlobalErrorListeners } from '@angular/core';
import { provideRouter, withInMemoryScrolling, withViewTransitions } from '@angular/router';
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import { DATE_PIPE_DEFAULT_OPTIONS } from '@angular/common';
import { firstValueFrom } from 'rxjs';

import { routes } from './app.routes';
import { provideClientHydration, withEventReplay } from '@angular/platform-browser';
import { API_BASE_URL } from './shared/api/api-config';
import { environment } from '../environments/environment';
import { authInterceptor } from './shared/api/auth-interceptor';
import { Auth } from './shared/services/auth';
import { AuthApiService } from './shared/services/auth-api.service';
import { reconcileOnboardingStage } from './shared/services/onboarding-stage';
import { DATE_FORMATS } from './shared/date-formats';

export const appConfig: ApplicationConfig = {
  providers: [
    provideBrowserGlobalErrorListeners(),
    provideRouter(
      routes,
      withViewTransitions(),
      // Landing's header nav links to same-page section fragments
      // (#modulos, #como, ...) — without this the URL fragment changes but
      // nothing scrolls.
      withInMemoryScrolling({ anchorScrolling: 'enabled', scrollPositionRestoration: 'enabled' }),
    ),
    provideClientHydration(withEventReplay()),
    provideHttpClient(withInterceptors([authInterceptor])),
    { provide: API_BASE_URL, useValue: environment.apiBaseUrl },
    { provide: DATE_PIPE_DEFAULT_OPTIONS, useValue: { dateFormat: DATE_FORMATS.date } },
    // Restores a session from the persisted refresh token before the
    // router's initial navigation resolves, so authGuard/roleGuard see it
    // immediately instead of racing it. No-op (no network call) when nothing
    // is stored, so anonymous/landing visits are unaffected. Calls refresh,
    // not perfil, because the access token itself is never persisted — see
    // shared/services/auth.ts for why.
    provideAppInitializer(async () => {
      const auth = inject(Auth);
      const authApi = inject(AuthApiService);
      const refreshToken = auth.refreshToken();
      if (!refreshToken) {
        return;
      }
      try {
        const session = await firstValueFrom(authApi.refresh(refreshToken));
        auth.applySession(session);
        // The refresh response's stage is a guess that would otherwise
        // overwrite the persisted one; correct it before the first render.
        if (auth.isAdminOs()) {
          await reconcileOnboardingStage(auth, authApi);
        }
      } catch {
        auth.clear();
      }
    }),
  ],
};
